import { Component, EventEmitter, Injectable, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { DateRange, MAT_DATE_RANGE_SELECTION_STRATEGY, MatDateRangePicker, MatDateRangeSelectionStrategy } from '@angular/material/datepicker';


@Injectable()
export class WeekRangeSelectionStrategy<D> implements MatDateRangeSelectionStrategy<D> {
  constructor(private _dateAdapter: DateAdapter<D>) { }

  selectionFinished(date: D | null): DateRange<D> {
    return this._createWeekRange(date);
  }

  createPreview(activeDate: D | null): DateRange<D> {
    return this._createWeekRange(activeDate);
  }

  private _createWeekRange(date: D | null): DateRange<D> {
    if (!date) return new DateRange<D>(null, null);

    const day = this._dateAdapter.getDayOfWeek(date) || 7; // 1=Monday in Material? getDayOfWeek returns 0 Sunday? check
    // Align to Monday as start of ISO week
    const start = this._dateAdapter.addCalendarDays(date, -((day + 6) % 7));
    const end = this._dateAdapter.addCalendarDays(start, 6);
    return new DateRange<D>(start, end);
  }
}

@Component({
  selector: 'teiasoft-week-picker',
  templateUrl: './teiasoft-week-picker.component.html',
  styleUrls: ['./teiasoft-week-picker.component.scss'],
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: WeekRangeSelectionStrategy,
    },
  ],
})
export class TeiasoftWeekPickerComponent implements OnInit {

  @Input() labelText: string = '';
  @Input() value?: string | number | null;
  @Input() isRequired: boolean = false;
  @Input() isValid: boolean = true;
  @Input() disabled: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() noMargin: boolean = false;
  @Input() noMinWidth: boolean = false;
  @Input() hideLabel: boolean = false;
  @Input() minDate: Date | null = null;

  @Output() onChange: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('picker') picker!: MatDateRangePicker<Date>;

  displayValue: string = '';
  selectedDate: Date | null = null;
  startAtDate: Date = new Date();

  ngOnInit(): void {
    if (this.value !== undefined && this.value !== null && this.value !== '') {
      this.setInitialValue(this.value);
    }
  }

  openPicker() {
    if (this.readOnly || this.disabled) return;
    this.picker.open();
  }

  onDateChange(picked: Date | null) {
    if (!picked) {
      this.selectedDate = null;
      this.displayValue = '';
      this.onChange.emit('');
      return;
    }

    const weekStart = this.getISOWeekStart(picked);
    this.selectedDate = weekStart;
    this.displayValue = this.formatWeekLabel(weekStart);
    this.onChange.emit(this.displayValue);
  }

  private setInitialValue(value: string | number) {
    if (typeof value === 'number') {
      const date = new Date(value);
      this.selectedDate = this.getISOWeekStart(date);
      this.displayValue = this.formatWeekLabel(this.selectedDate);
      this.startAtDate = this.selectedDate;
      return;
    }

    const parsed = this.getDateFromWeekLabel(value);
    if (parsed) {
      this.selectedDate = parsed;
      this.displayValue = this.formatWeekLabel(parsed);
      this.startAtDate = parsed;
    }
  }

  private getISOWeekStart(date: Date): Date {
    const temp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = temp.getUTCDay() || 7;
    temp.setUTCDate(temp.getUTCDate() - day + 1);
    return temp;
  }

  private getISOWeek(date: Date): { week: number; year: number } {
    const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = tempDate.getUTCDay() || 7;
    tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum);
    const year = tempDate.getUTCFullYear();
    const yearStart = new Date(Date.UTC(year, 0, 1));
    const week = Math.ceil((((tempDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return { week, year };
  }

  private formatWeekLabel(date: Date): string {
    const { week, year } = this.getISOWeek(date);
    const weekStr = week.toString().padStart(2, '0');
    const yearStr = year.toString().slice(-2);
    return `W${weekStr}-${yearStr}`;
  }

  private getDateFromWeekLabel(label: string): Date | null {
    const match = /^W(\d{2})-(\d{2})$/.exec(label.trim());
    if (!match) return null;
    const week = parseInt(match[1], 10);
    const year = 2000 + parseInt(match[2], 10);
    const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
    const dayOfWeek = simple.getUTCDay();
    const ISOWeekStart = new Date(simple);
    if (dayOfWeek <= 4)
      ISOWeekStart.setUTCDate(simple.getUTCDate() - simple.getUTCDay() + 1);
    else
      ISOWeekStart.setUTCDate(simple.getUTCDate() + 8 - simple.getUTCDay());
    return ISOWeekStart;
  }
}
